import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { EAnswerVote } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import {
  useAnswerUpvote_UpvoteAnswer,
  useAnswerUpvote_UpvoteAnswerVariables,
} from './graphql-types/useAnswerUpvote_UpvoteAnswer';
import { useAnswerVote_AnswerFragment } from './graphql-types/useAnswerVote_AnswerFragment';

const ANSWER_FRAGMENT = gql`
  fragment useAnswerVote_AnswerFragment on AnswerV2 {
    _id
    currentUserVote
    tags {
      _id
    }
    announcement {
      _id
    }
    sponsorData {
      audience {
        group {
          _id
        }
      }
    }
    group {
      _id
      isGlobalSingleton
    }
    organization {
      _id
    }
    event {
      _id
    }
  }
`;

const VOTE = gql`
  mutation useAnswerUpvote_UpvoteAnswer($postId: String!, $vote: EAnswerVote!) {
    modifyVoteOnAnswer(input: { answerPostId: $postId, vote: $vote }) {
      result {
        numUpvotes
        numDownvotes
        ...useAnswerVote_AnswerFragment
      }
    }
  }
  ${ANSWER_FRAGMENT}
`;
export const useVoteButton: {
  (answer: useAnswerVote_AnswerFragment): {
    handleVote: (answerVote: EAnswerVote) => Promise<void>;
  };
  answerFragment: DocumentNode;
} = (
  answer: useAnswerVote_AnswerFragment
): {
  handleVote: (answerVote: EAnswerVote) => Promise<void>;
} => {
  const toast = useToast();
  const [vote] = useMutation<
    useAnswerUpvote_UpvoteAnswer,
    useAnswerUpvote_UpvoteAnswerVariables
  >(VOTE);

  const analyticsClient = useAnalyticsLogger();

  const handleVote = async (answerVote: EAnswerVote): Promise<void> => {
    try {
      await vote({
        variables: {
          postId: answer._id,
          vote:
            answerVote === answer.currentUserVote
              ? EAnswerVote.NONE
              : answerVote,
        },
      });
      if (
        answerVote !== answer.currentUserVote &&
        (answerVote === EAnswerVote.UPVOTE ||
          answerVote === EAnswerVote.DOWNVOTE)
      ) {
        analyticsClient.logEvent_comm_click_like({
          'added tags': !!answer?.tags && answer.tags.length > 0,
          announcement: !!answer?.announcement?._id || false,
          'posted in': answer?.event
            ? 'event'
            : answer?.organization
            ? 'organization'
            : answer?.group?.isGlobalSingleton
            ? 'general'
            : 'group',
          'is hosted by sponsor': !!answer?.sponsorData?.audience.group,
          'post type': 'answer',
          'on post': true,
          'reaction type':
            answerVote === EAnswerVote.UPVOTE ? 'upvote' : 'downvote',
        });
      }
    } catch (err) {
      logError(err);
      toast({
        description: 'Failed to update vote on answer',
        status: 'error',
      });
    }
  };
  return {
    handleVote,
  };
};

useVoteButton.answerFragment = ANSWER_FRAGMENT;
